import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Mail, Phone, MapPin, Send, LayoutDashboard } from 'lucide-react';
import { useReservation } from '../context/ReservationContext';
import { publicApi } from '../lib/api';

const Footer = () => {
  const { openReservation } = useReservation();
  const [contact, setContact] = useState({
    contact_address: 'FJ98+J3F, Porto-Novo, Bénin',
    contact_maps_url: '',
    contact_phone: '+229 01 95 92 70 77',
    contact_email: 'servicesclient@ols-pierreedois.com',
    contact_whatsapp: '22901959270077',
  });
  useEffect(() => { publicApi.getContactSettings && publicApi.getContactSettings().then(setContact).catch(() => {}); }, []);
  return (
    <>
      {/* CTA banner */}
      <section className="relative overflow-hidden">
        <div className="bg-gradient-to-br from-[#F4A340] to-[#E89534] py-16 px-6">
          <div className="max-w-5xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Prêt pour l'aventure ?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
              Réservez votre balade en quelques clics et embarquez vers une expérience unique.
            </p>
            <button
              onClick={() => openReservation()}
              className="inline-flex items-center gap-3 px-10 py-4 bg-white hover:bg-slate-900 text-slate-900 hover:text-white font-semibold rounded-full shadow-lg transition-all duration-300 hover:-translate-y-0.5"
            >
              Réserver maintenant
              <Send className="w-4 h-4" />
            </button>
          </div>
          {/* Decorative diamonds */}
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rotate-45 bg-white/10 rounded-3xl"></div>
          <div className="absolute -top-10 -right-10 w-32 h-32 rotate-45 bg-white/10 rounded-3xl"></div>
        </div>
      </section>

      <footer id="contact" className="bg-slate-900 text-slate-300 pt-16 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rotate-45 bg-[#F4A340] rounded-md flex items-center justify-center shadow-lg">
                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                    <span className="-rotate-45 text-slate-900 font-bold text-xl italic">p</span>
                  </div>
                </div>
                <div>
                  <div className="text-xl font-bold text-white">Pierre &amp; Edois</div>
                  <div className="text-[10px] tracking-[0.2em] text-slate-500 font-medium">OLS · VISONS PLUS LOIN</div>
                </div>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Les balades lagunaires les plus authentiques du Bénin, entre tradition et modernité.
              </p>
            </div>

            {/* Nav */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-widest">Navigation</h4>
              <ul className="space-y-2 text-sm">
                {['Accueil', 'Nos balades', 'Expérience', 'Galerie', 'Avis', 'FAQ'].map((l) => (
                  <li key={l}>
                    <a href={`/#${l.toLowerCase()}`} className="hover:text-[#F4A340] transition-colors">{l}</a>
                  </li>
                ))}
                <li>
                  <Link to="/suivi" className="hover:text-[#F4A340] transition-colors">Suivre ma commande</Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-widest">Contact</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#F4A340] mt-0.5 flex-shrink-0" />
                  {contact.contact_maps_url ? (
                    <a href={contact.contact_maps_url} target="_blank" rel="noreferrer" className="hover:text-white transition" data-testid="footer-address">{contact.contact_address}</a>
                  ) : (
                    <span data-testid="footer-address">{contact.contact_address}</span>
                  )}
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-[#F4A340] flex-shrink-0" />
                  <a href={`tel:${(contact.contact_phone || '').replace(/\s/g, '')}`} className="hover:text-white transition" data-testid="footer-phone">{contact.contact_phone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#F4A340] flex-shrink-0" />
                  <a href={`mailto:${contact.contact_email}`} className="hover:text-white transition" data-testid="footer-email">{contact.contact_email}</a>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-widest">Newsletter</h4>
              <p className="text-sm text-slate-400 mb-4">Recevez nos meilleures offres et actualités.</p>
              <form className="flex gap-2" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="email"
                  placeholder="Votre email"
                  className="flex-1 min-w-0 px-4 py-2.5 rounded-full bg-slate-800 border border-slate-700 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#F4A340] transition-colors"
                />
                <button className="w-11 h-11 rounded-full bg-[#F4A340] hover:bg-[#E89534] text-white flex items-center justify-center transition-colors flex-shrink-0">
                  <Send className="w-4 h-4" />
                </button>
              </form>
              <div className="flex gap-3 mt-6">
                {[Instagram, Facebook, Twitter].map((Icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-[#F4A340] flex items-center justify-center transition-colors">
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
            <div>© {new Date().getFullYear()} Pierre &amp; Edois · Tous droits réservés</div>
            <div className="flex gap-6 items-center">
              <Link to="/admin" className="flex items-center gap-1.5 hover:text-[#F4A340] transition-colors">
                <LayoutDashboard className="w-3.5 h-3.5" /> Admin
              </Link>
              <a href="#" className="hover:text-white transition-colors">Mentions légales</a>
              <a href="#" className="hover:text-white transition-colors">Confidentialité</a>
              <a href="#" className="hover:text-white transition-colors">CGV</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a
        href="https://wa.me/22990000000"
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20BA5A] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
        aria-label="WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M12.05 2C6.51 2 2 6.51 2 12.05c0 1.76.46 3.48 1.34 5L2 22l5.11-1.34c1.47.8 3.13 1.22 4.83 1.22h.11c5.54 0 10.05-4.51 10.05-10.05C22.1 6.51 17.59 2 12.05 2zm5.85 14.19c-.25.71-1.44 1.35-2.02 1.44-.52.08-1.17.11-1.89-.12-.44-.14-1-.32-1.72-.63-3.02-1.31-4.99-4.35-5.14-4.55-.15-.2-1.22-1.62-1.22-3.1s.77-2.2 1.05-2.5c.28-.31.6-.38.8-.38.2 0 .4 0 .58.01.18.01.44-.07.68.52.25.6.85 2.09.93 2.24.08.15.13.33.03.53-.1.2-.15.32-.3.5-.15.18-.31.4-.44.53-.15.15-.3.31-.13.61.17.3.77 1.27 1.65 2.05 1.13 1 2.08 1.31 2.38 1.46.3.15.47.13.65-.08.18-.2.75-.87.95-1.17.2-.3.4-.25.68-.15.28.1 1.77.83 2.07.98.3.15.5.23.58.35.08.13.08.73-.17 1.44z" />
        </svg>
      </a>
    </>
  );
};

export default Footer;
